// Weekly posts view card (feature 004, restyled in feature 005 "intelly"). Presentational only —
// takes a WeekPostCardModel + optional action callbacks, no Redux/FullCalendar access, so it is
// unit-testable in isolation. The ⋮ menu items call the callbacks the board wires to existing flows.
import React from "react";
import {Dropdown} from "react-bootstrap";
import {getHandlerIconByCode} from "../../helpers/functions";
import {PeerActivityStatus} from "../../redux/Content/PeerActivityStatus/types";
import {WeekPostCardModel} from "./WeekPostCard.helpers";

interface Props {
    model: WeekPostCardModel;
    // Wired by the board/host (feature 005 US2). Optional so the card renders standalone.
    onEdit?: () => void;
    onDuplicate?: () => void;
    onDelete?: () => void;
    onRetry?: () => void;
}

const stop = (e: React.MouseEvent) => e.stopPropagation();

const WeekPostCard: React.FC<Props> = ({model, onEdit, onDuplicate, onDelete, onRetry}) => {
    const isError = model.status === PeerActivityStatus.STATUS_ERROR;

    if (model.isCollapsed) {
        return (
            <div className="weekPostCard weekPostCard--collapsed" data-status={model.status}>
                <span className="weekPostCard__time">{model.startTime}</span>
                <span className="weekPostCard__title text-truncate" title={model.title}>{model.title}</span>
                {model.statusLabel && <span className="visually-hidden">{model.statusLabel}</span>}
            </div>
        );
    }

    const rootClass = "weekPostCard"
        + (isError ? " weekPostCard--error" : "")
        + (model.isDraft ? " weekPostCard--draft" : "");

    return (
        <div className={rootClass} data-status={model.status}>
            <div className="weekPostCard__head">
                <div className="weekPostCard__headLeft">
                    {model.platformCode
                        ? <img className="weekPostCard__platform rounded-circle" src={getHandlerIconByCode(model.platformCode)} alt={model.platformName}/>
                        : <span className="weekPostCard__platform weekPostCard__platform--fallback rounded-circle" aria-hidden="true">
                            {model.platformName ? model.platformName[0] : "?"}
                        </span>}
                    {model.startTime && <span className="weekPostCard__time">{model.startTime}</span>}
                </div>

                {/* Right slot: the status pill shows by default; on hover/focus the actions group
                    (a status-coloured dot + the ⋮ manage button) is revealed in its place. */}
                <div className="weekPostCard__headRight">
                    {model.statusLabel &&
                        <span className={"weekPostCard__statusPill badge badge-soft-" + model.statusClass}>{model.statusLabel}</span>}
                    <div className="weekPostCard__actions">
                        <span className={"weekPostCard__statusDot weekPostCard__statusDot--" + model.statusClass}
                              title={model.statusLabel} aria-hidden="true"/>
                        <Dropdown className="weekPostCard__menuWrap" onClick={stop}>
                            <Dropdown.Toggle as="button" type="button" bsPrefix="weekPostCard__menu" aria-label="Post actions">
                                <i className="ri-more-2-fill" aria-hidden="true"/>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" renderOnMount className="weekPostCard__menuList">
                                <Dropdown.Item className="weekPostCard__mi weekPostCard__mi--edit" onClick={() => onEdit?.()}>
                                    <i className="ri-pencil-fill me-2"/>Edit
                                </Dropdown.Item>
                                <Dropdown.Item className="weekPostCard__mi" onClick={() => onDuplicate?.()}>
                                    <i className="ri-file-copy-line me-2"/>Duplicate
                                </Dropdown.Item>
                                {isError &&
                                    <Dropdown.Item className="weekPostCard__mi" onClick={() => onRetry?.()}>
                                        <i className="ri-refresh-line me-2"/>Retry
                                    </Dropdown.Item>}
                                <Dropdown.Divider/>
                                <Dropdown.Item className="weekPostCard__mi text-danger" onClick={() => onDelete?.()}>
                                    <i className="ri-delete-bin-fill me-2"/>Delete
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </div>

            {model.channelName &&
                <div className="weekPostCard__channel text-truncate" title={model.channelName}>{model.channelName}</div>}

            {model.title &&
                <div className="weekPostCard__title text-truncate" title={model.title}>{model.title}</div>}

            {model.text &&
                <div className="weekPostCard__text" title={model.text}>{model.text}</div>}

            <div className="weekPostCard__foot">
                {model.channelInitials &&
                    <span className="weekPostCard__avatars">
                        <span className="weekPostCard__av" data-av={model.channelColorKey} title={model.channelName} aria-hidden="true">
                            {model.channelInitials}
                        </span>
                    </span>}
                {isError &&
                    <button type="button" className="weekPostCard__retry" onClick={(e) => {stop(e); onRetry?.();}}>
                        <i className="ri-refresh-line" aria-hidden="true"/> Retry
                    </button>}
                {model.mediaLabel &&
                    <span className="weekPostCard__media">
                        <i className={(model.media.isVideo ? "ri-play-circle-fill" : "ri-image-2-fill")} aria-hidden="true"/>
                        {model.mediaLabel}
                    </span>}
            </div>
        </div>
    );
};

export default WeekPostCard;
